import "./styles.css";

import { fetchData, fetchWhoAmI } from "./api";
import { renderFiltersBar } from "./filters";
import {
  applyFilters,
  DEFAULT_ACTIVE_TAB,
  getState,
  readFiltersFromUrl,
  setActiveTab,
  setData,
  setUser,
  subscribe,
} from "./state";
import type { ActiveTab } from "./state";
import type { OpportunityRow } from "./types";
import { renderAdminTab } from "./views/admin";
// SGNL-207: stage_velocity + conversion_funnel are no longer top-level
// TAB_RENDERERS entries — they're sub-tabs of Pipeline Analytics.
// pipeline_analytics.ts imports the renderers directly from analytics.ts;
// main.ts no longer needs them.
import { renderPocExplorerTab } from "./views/poc_explorer";
import { renderAIBreakdownTab } from "./views/ai_breakdown";
import { renderCustomersTab } from "./views/customers";
import { renderDealsTab } from "./views/deals";
import { renderFarmingTab } from "./views/farming";
import { renderHygieneTab } from "./views/hygiene";
import { renderKPIs } from "./views/kpis";
import { renderLostTab } from "./views/lost";
import { renderMaturityTab } from "./views/maturity";
import { renderNoAccessPage } from "./views/no_access";
import { renderPipelineAnalyticsTab } from "./views/pipeline_analytics";
// SGNL-201: Hub UI parked — see docs/SGNL-201-hub-decoupling.md.
// The hub.ts / app_detail.ts modules + their tests stay in the tree so
// the work can be relocated to the future hub.bounteous.tools project
// without re-deriving the views. They're intentionally NOT imported
// here — removing the route dispatch is what decouples them from
// Signal SPA.
import { renderOppDetailPage } from "./views/opp_detail";
// SGNL-205: single-source hash router. parseRoute returns a Route
// discriminated union; renderEverything switches on `kind`. Replaces
// the ad-hoc parseOppHash check below + the implicit "fall through
// to state.activeTab" branch. See router.ts for the rationale.
import { parseRoute } from "./router";
import { applyChipView, currentChipView } from "./views/deal_chips";
import { gateAdminTab, isCurrentUserAdmin } from "./auth_guards";
import { renderPartnersTab } from "./views/partners";
import { renderReportsTab } from "./views/reports";
import { renderRevenueCalendarTab } from "./views/revenue_calendar";
import { renderWeeklyCallTab } from "./views/weekly_call";

const filtersHost = document.getElementById("filters")!;
const kpisHost = document.getElementById("kpis")!;
const tabHost = document.getElementById("tab-host")!;
const tabsNav = document.querySelector<HTMLElement>(".tabs")!;
const heroInner = document.querySelector<HTMLElement>(".hero-inner")!;

function renderUserChip(): void {
  const { user } = getState();
  if (!user) return;
  if (heroInner.querySelector(".user-chip")) return;
  const chip = document.createElement("div");
  chip.className = "user-chip";
  // SGNL-201: "Hub" link removed — the Hub launcher will move to a
  // separate hub.bounteous.tools project. The link comes back (likely
  // as an absolute https://hub.bounteous.tools/ href) when that ships.
  chip.innerHTML = `
    <span class="user-name">${user.name}</span>
    <a class="logout" href="/logout">Sign out</a>
  `;
  heroInner.appendChild(chip);
}

// SGNL-208 — admin tab gating is centralised in ./auth_guards.ts
// (testable; re-usable from any future session-refresh handler).

function renderActiveTabButton(activeTab: ActiveTab = getState().activeTab): void {
  // SGNL-208 PR-Agent (importance 6): accept the effective tab as a
  // parameter so renderEverything can pass the post-fallback tab here
  // — keeping the highlighted button in sync with the rendered body.
  // Default to state.activeTab for callers that don't apply any
  // post-state override (no-op for the existing call sites).
  for (const btn of tabsNav.querySelectorAll<HTMLButtonElement>(".tab")) {
    btn.classList.toggle("active", btn.dataset.tab === activeTab);
  }
}

function renderLastIngested(meta: { last_ingested_at: string | null }): void {
  const el = document.querySelector<HTMLElement>('[data-testid="last-ingested"]');
  if (!el) return;
  if (!meta.last_ingested_at) {
    el.textContent = "Last ingested: never";
    return;
  }
  const d = new Date(meta.last_ingested_at);
  // Guard against malformed timestamp strings — `new Date(...)` returns
  // Invalid Date silently, and `.toISOString()` on Invalid Date THROWS,
  // which would bubble up and crash the whole render. Caught by PR-Agent
  // on PR #13.
  if (Number.isNaN(d.getTime())) {
    el.textContent = "Last ingested: unknown";
    return;
  }
  const iso = d.toISOString();
  el.textContent = `Last ingested · ${iso.slice(0, 10)} ${iso.slice(11, 16)} UTC`;
}

function renderEverything(): void {
  const { data } = getState();
  if (!data) {
    tabHost.innerHTML = `<div class="loading">Loading data…</div>`;
    return;
  }
  // SGNL-205 — renderEverything is a PURE render. It NEVER mutates
  // state from the hash. State-mutating restoration (hash → activeTab
  // or hash → chip filter) happens in restoreFromHash(), called from
  // init() at boot and the hashchange listener. Without this split,
  // any setFilters → notify → renderEverything cycle would re-read
  // the (possibly stale) hash and re-apply chip state, which would
  // override the user's just-set manual filter edit. See the architect
  // F-01 review on PR #123 for the failure trace.
  const route = parseRoute(window.location.hash);

  if (route.kind === "opp") {
    // Opp detail is a pure render path: no state mutation, just mount
    // the detail view inside the tab host while keeping filters + KPIs
    // visible above.
    renderFiltersBar(filtersHost, data.meta);
    renderLastIngested(data.meta);
    renderKPIs(kpisHost, applyFilters(data.opps, getState().filters));
    void renderOppDetailPage(tabHost, route.id);
    return;
  }

  // For kind="tab" and kind="none" — render from state.activeTab +
  // state.filters. restoreFromHash() will have already synced the
  // tab + chip view into state before this point on boot / hashchange.
  renderFiltersBar(filtersHost, data.meta);
  renderLastIngested(data.meta);
  const filtered = applyFilters(data.opps, getState().filters);
  renderKPIs(kpisHost, filtered);
  let tab = getState().activeTab;
  // SGNL-208: defence-in-depth — if a non-admin lands on the admin tab
  // (e.g. via a stale bookmark or pasted #admin URL), the admin
  // endpoints would 403 with a raw error in the body. Force the
  // fallback so non-admins see a clean view, never the broken state.
  // The Admin tab button is already removed by gateAdminTab for the
  // common path; this catches the URL-paste edge case.
  // PR-Agent (importance 6): compute the effective tab FIRST so the
  // active-button toggle below matches the rendered body. Otherwise
  // renderActiveTabButton would highlight the un-overridden tab (e.g.
  // "admin"), which has been removed from the DOM — UI shows no active
  // tab while the body shows AI breakdown.
  if (tab === "admin" && !isCurrentUserAdmin()) {
    tab = DEFAULT_ACTIVE_TAB;
  }
  renderActiveTabButton(tab);
  // PRA-1: tolerate a hydrated/URL-injected tab outside the union by
  // falling back to placeholder instead of throwing.
  const render = TAB_RENDERERS[tab] ?? TAB_RENDERERS.placeholder;
  render(tabHost, filtered);
}

/** SGNL-205 — Apply hash-driven state. Called ONCE on boot (after data
 * is loaded) and on every hashchange (Back/Forward, pasted-into-URL-bar).
 * Never called from renderEverything: that would re-create the F-01 race
 * where a notify-driven re-render reverts a manual filter edit.
 *
 * Returns `true` if state WAS MUTATED (setActiveTab or applyChipView
 * fired), in which case the subscribe → renderEverything chain has
 * already run and the caller MUST NOT call renderEverything itself
 * (double-render). Returns `false` when state was unchanged — caller
 * should call renderEverything so the view reflects the new hash
 * (e.g. a pipeline_analytics sub-tab swap where activeTab is already
 * pipeline_analytics: no state mutation, but the wrapper needs to
 * re-render to pick up the new sub from the hash).
 *
 * SGNL-207 PR-Agent (importance 8): the previous semantics ("did a
 * redirect happen?") incorrectly skipped renderEverything when the
 * redirect landed on a no-op state, leaving stale view content. The
 * correct contract is "should the caller skip its render", which is
 * a different question that this signature now answers correctly. */
function restoreFromHash(depth = 0): boolean {
  // SGNL-207 (Arch F-01 / CR F-05): defence-in-depth bound on
  // recursive redirect resolution. REDIRECT_MAP values are static
  // strings and the test pins they're terminal — but if a future
  // PR accidentally chains redirects, this prevents a stack-overflow
  // SPA crash. 5 is generous; any legitimate redirect chain longer
  // than 1 is a design error.
  if (depth > 5) {
    console.error(
      "restoreFromHash: redirect depth exceeded, possible loop in REDIRECT_MAP. Hash:",
      window.location.hash,
    );
    return false;
  }
  const route = parseRoute(window.location.hash);
  // SGNL-207: compatibility redirect. Replace the URL with the target
  // hash and re-enter so the new hash is processed by its real kind.
  // history.replaceState (not pushState) so Back doesn't loop into
  // the deprecated hash. Propagate the recursive return value — the
  // OUTER redirect call is a state-mutator iff the resolved route
  // mutated state.
  if (route.kind === "redirect") {
    history.replaceState(
      history.state,
      "",
      window.location.pathname + window.location.search + route.target,
    );
    return restoreFromHash(depth + 1);
  }
  if (route.kind === "tab") {
    let mutated = false;
    if (getState().activeTab !== route.tab) {
      setActiveTab(route.tab);
      mutated = true;
    }
    if (route.tab === "deals") {
      const view = route.params.get("view");
      // applyChipView is idempotent (state.ts:setFilters has a same-value
      // guard), so the call is safe even when the chip is already the
      // active one. Skipping the no-op call avoids a wasted setFilters
      // round-trip on every hashchange.
      if (view && currentChipView() !== view) {
        applyChipView(view);
        mutated = true;
      }
    }
    return mutated;
  }
  // kind === "opp" or "none": no state mutation; caller renders.
  return false;
}

type TabRenderer = (host: HTMLElement, filtered: OpportunityRow[]) => void;

const TAB_RENDERERS: Record<ActiveTab, TabRenderer> = {
  ai: (host, filtered) => renderAIBreakdownTab(host, filtered),
  farming: (host, filtered) => renderFarmingTab(host, filtered),
  customers: (host, filtered) => renderCustomersTab(host, filtered),
  hygiene: (host, filtered) => renderHygieneTab(host, filtered),
  lost: (host, filtered) => renderLostTab(host, filtered),
  deals: (host, filtered) => renderDealsTab(host, filtered),
  weekly_call: (host, filtered) => {
    void renderWeeklyCallTab(host, filtered);
  },
  partners: (host, filtered) => renderPartnersTab(host, filtered),
  maturity: (host, filtered) => renderMaturityTab(host, filtered),
  admin: (host, filtered) => renderAdminTab(host, filtered),
  poc_explorer: (host) => {
    void renderPocExplorerTab(host);
  },
  // SGNL-206 — Pipeline Analytics wraps Stage Velocity + Funnel as
  // sub-tabs. SGNL-207 removed the standalone TAB_RENDERERS entries
  // for stage_velocity / conversion_funnel since they have no nav
  // button and parseRoute redirects their hashes here. The
  // renderStageVelocityTab + renderConversionFunnelTab imports
  // remain — pipeline_analytics.ts:mountSub delegates to them.
  pipeline_analytics: (host) => {
    void renderPipelineAnalyticsTab(host);
  },
  reports: (host, filtered) => {
    void renderReportsTab(host, filtered);
  },
  // SGNL-230 — Revenue Calendar is data-driven from its own endpoints, not
  // from the DataBundle slice the other tabs consume. The renderer ignores
  // `filtered` and fetches its own state.
  revenue_calendar: (host) => {
    void renderRevenueCalendarTab(host);
  },
  placeholder: (host) => {
    host.innerHTML = `<div class="placeholder">Coming in a later phase.</div>`;
  },
};

// SGNL-202: hash-routes that represent full-page detail overlays and
// should be cleared when the user clicks a top-nav tab. Match-only
// (not parsed) — the parsers (parseOppHash etc.) own the validation.
//
// `#opp/` — deal detail (live)
// `#apps/` — App Detail (parked under SGNL-201; included for future
//            unparking + defence against a regression that brings
//            the route back without re-adding the dispatch handler)
// `#hub` — Hub launcher (parked under SGNL-201; same rationale)
//
// Generic-namespace hashes like SGNL-205's `#deals?view=lost` (saved-
// view chip) MUST NOT match here — those carry filter state that
// shouldn't be stripped on a tab click.
const DETAIL_ROUTE_HASH = /^#(opp\/|apps\/|hub(?:$|[?]))/;

// SEC-01 / PRA-2: hasOwnProperty.call (broader browser support than
// Object.hasOwn) so DevTools-injected data-tab="__proto__" can't pass.
for (const btn of tabsNav.querySelectorAll<HTMLButtonElement>(".tab[data-tab]")) {
  btn.addEventListener("click", () => {
    const raw = btn.dataset.tab;
    if (raw && Object.prototype.hasOwnProperty.call(TAB_RENDERERS, raw)) {
      // SGNL-202: if a detail-route hash is active (e.g. #opp/<id>),
      // clear it so the tab takes over. Without this, the route
      // dispatch in renderEverything keeps rendering the detail page
      // even though setActiveTab fired — tabs appear inert from a
      // deal detail page.
      //
      // Architect / PR-Agent (importance 7): the guard is NARROW —
      // only matches detail-route patterns (#opp/, #apps/, #hub).
      // Generic SPA hashes that may come later (e.g. SGNL-205's
      // `#deals?view=lost` saved-view chip) must NOT be cleared by a
      // tab click; clearing them would silently strip bookmarkable
      // filter state.
      //
      // history.replaceState (not pushState) avoids a back-history
      // entry — Back should land where the user was before opening
      // the detail page, not on the parked detail page.
      // history.replaceState does NOT fire hashchange — so the
      // setActiveTab call below is the single render trigger,
      // avoiding a double-render race.
      //
      // The 3rd arg preserves `window.location.search` deliberately
      // so any global query params (auth tokens, feature flags) survive.
      // `history.state` is passed through so other SPA components that
      // depend on it (none today) don't see a surprise reset.
      const hadDetailHash = DETAIL_ROUTE_HASH.test(window.location.hash);
      if (hadDetailHash) {
        history.replaceState(
          history.state,
          "",
          window.location.pathname + window.location.search,
        );
      }
      // SGNL-206 (CR F-03): the tab button on a multi-sub tab (e.g.
      // Pipeline Analytics) should reset the user to the default
      // sub when re-clicked. Without this, the same-value guard in
      // setActiveTab (F-02) makes the re-click a silent no-op and
      // the user is stuck on the prior sub. Clear the sub from the
      // hash when the same tab button is clicked.
      if (
        raw === "pipeline_analytics" &&
        getState().activeTab === "pipeline_analytics" &&
        window.location.hash.startsWith("#pipeline_analytics?")
      ) {
        history.replaceState(
          history.state,
          "",
          window.location.pathname + window.location.search + "#pipeline_analytics",
        );
        renderEverything();
        return;
      }
      setActiveTab(raw as ActiveTab);
      // SGNL-205 regression guard: setActiveTab now has a same-value
      // guard (F-02). If the user clicks the SAME tab they were on
      // before opening a deal detail (e.g. clicked an opp from Deal
      // Explorer → opp detail open → click Deal Explorer tab again),
      // activeTab is already "deals" so setActiveTab no-ops and no
      // re-render fires. The hash was just cleared but the opp-detail
      // content remains in tabHost. Force a render in that case.
      if (hadDetailHash) {
        renderEverything();
      }
    }
  });
}

subscribe(() => renderEverything());

// SGNL-228 — re-fetch the bundle when the pre-qual toggle flips. State
// notifies; we compare against the last-fetched value to detect a real
// flip (not just an idempotent setIncludePreQual call OR a re-emission
// from setData's own notify after we land the new bundle). Re-fetching
// here (not in state.ts) keeps state pure + dependency-free.
//
// Pre-PR security-SEC-228-05 / code-reviewer CQ-05: the sentinel
// assignment MUST happen BEFORE the await — setData() inside the await
// chain calls notify() which re-fires this same subscriber, and only
// the eager assignment makes the guard see "we're already fetching
// for this value" on the re-entry. Moving the assignment after the
// await would introduce an infinite re-fetch loop with no other
// circuit breaker.
let _lastFetchedIncludePreQual = false;
// PR-Agent #145 (imp 7): monotonic sequence number guards against
// out-of-order responses if the operator clicks the toggle rapidly.
// Older in-flight requests resolve later; without the seq check, a
// slower stale response would overwrite a newer one. Drop any
// response whose seq isn't the latest issued.
let _bundleFetchSeq = 0;
subscribe(async (s) => {
  if (s.includePreQual === _lastFetchedIncludePreQual) return;
  _lastFetchedIncludePreQual = s.includePreQual; // load-bearing ordering
  const seq = ++_bundleFetchSeq;
  try {
    const data = await fetchData({ includePreQual: s.includePreQual });
    if (seq !== _bundleFetchSeq) return; // stale response — newer fetch superseded
    setData(data);
  } catch (e) {
    if (seq !== _bundleFetchSeq) return; // also drop stale errors
    // Don't toggle back — operator sees the (now-stale) bundle but the
    // FE doesn't crash. Refresh recovers. Could surface a toast in a
    // future polish pass; out of scope today.
    console.error("[SGNL-228] re-fetch on pre-qual toggle failed", e);
  }
});

// SGNL-84 — hash changes (e.g. clicking an opp name in Deal Explorer or
// Admin grid → "#opp/006xxx", or hitting Back) should also drive a
// re-render so the detail-page mount switches in/out without requiring
// a state change.
// SGNL-205 — also re-apply hash-driven state on hashchange (paste-into-
// URL, Back/Forward). restoreFromHash runs BEFORE renderEverything so
// the render reflects the just-applied state on the same tick.
window.addEventListener("hashchange", () => {
  // SGNL-207 (CR F-02 / PR-Agent importance 8): restoreFromHash
  // returns true iff state was mutated (which triggered subscribe
  // → renderEverything already). Skip our own render in that case
  // to avoid a double-render. When it returns false, we MUST render
  // — the hash changed but state didn't (e.g. a pipeline_analytics
  // sub swap), and only renderEverything will re-mount the view
  // with the new hash.
  const stateMutated = restoreFromHash();
  if (!stateMutated) renderEverything();
});

// Hub SSO handoff (consumer): if Hub launched us with `#hub_token=<JWT>`,
// POST it to the backend to establish a Signal session, scrub the fragment
// (so the token never lingers in history / re-runs), then reload so init()
// re-enters with the fresh session cookie. Failures fall through to the
// normal login gate — the handoff is best-effort, never a hard dependency.
async function consumeHubToken(): Promise<boolean> {
  const m = /(?:^|[#&])hub_token=([^&]+)/.exec(window.location.hash);
  if (!m) return false;
  const token = decodeURIComponent(m[1]);
  // Scrub the fragment immediately, regardless of outcome.
  history.replaceState(null, "", window.location.pathname + window.location.search);
  try {
    const r = await fetch("/api/auth/hub-token", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ token }),
    });
    if (r.ok) {
      window.location.reload();
      return true;
    }
  } catch {
    /* fall through to the normal login gate */
  }
  return false;
}

async function init(): Promise<void> {
  // Hub launch handoff — consume `#hub_token` before anything else.
  if (await consumeHubToken()) return;

  // SGNL-215 — /login is a static SPA route owned by views/login.ts.
  // When the user lands there (typically because they just signed
  // out or hit it directly), skip the whoami round-trip and render
  // the login surface. The login flow itself navigates to "/" on
  // success, which re-enters init() with the new session cookie.
  if (window.location.pathname === "/login") {
    const { renderLoginPage } = await import("./views/login");
    renderLoginPage(document.body);
    return;
  }

  // 1. Auth gate
  let user;
  try {
    user = await fetchWhoAmI();
  } catch (e) {
    tabHost.innerHTML = `<div class="error">Cannot reach backend: ${(e as Error).message}</div>`;
    return;
  }
  if (!user) {
    // SGNL-215 — send unauthenticated users to /login (the new chooser
    // page) rather than directly to /auth/login (the SSO endpoint).
    // /login surfaces both SSO + the username/password fallback so
    // external collaborators have a path that doesn't require Azure
    // AD. The SSO button on /login still goes to /auth/login.
    window.location.href = "/login";
    return;
  }
  setUser(user);

  // SGNL-208 — hide Admin tab from non-admin users so they don't see
  // a button that 403s on click. Runs immediately after setUser so
  // the window between "state.user available" and "admin button
  // removed" is the smallest possible (closes a theoretical race
  // where a hashchange event during init could trigger a render
  // with the admin button still in the DOM). Architect F-04 review.
  gateAdminTab(tabsNav);

  // SGNL-191 — front-door access gate. If the caller doesn't have
  // Signal access yet, replace the SPA shell with a stripped no-access
  // page (Request Access CTA + status). Skips KPI / data fetch so the
  // user never sees a /api/data 403 in the body. Q-06 (PR #111 code
  // review): exhaustive match on the known union states ('pending' |
  // 'none') rather than a `!== 'granted'` negation. TypeScript narrows
  // the union, and a future state added to the union becomes a compile
  // error here that forces an explicit decision. Missing field (pre-
  // SGNL-191 cached SPA bundle) → bypasses the gate (back-compat).
  if (user.signal_access === "pending" || user.signal_access === "none") {
    document.body.classList.add("signal-no-access");
    renderNoAccessPage(tabHost, user);
    return;
  }

  renderUserChip();

  // 2. Filters from URL
  readFiltersFromUrl();

  // 3. Fetch data
  try {
    const data = await fetchData();
    setData(data);
  } catch (e) {
    tabHost.innerHTML = `<div class="error">Failed to load data: ${(e as Error).message}</div>`;
    return;
  }

  // 4. SGNL-205 — apply hash-driven state ONCE after data is in place.
  // Examples: opening a fresh tab on /#deals?view=lost lands the user
  // on the Lost saved view; opening /#opp/{id} renders the detail page.
  // restoreFromHash runs after setData so the subsequent setActiveTab /
  // applyChipView calls operate on the populated state. Their notify()
  // will trigger renderEverything via subscribe — no manual render call
  // needed here.
  restoreFromHash();
}

void init();
